
import { useEffect, useState } from "react";

import useMaster from "hook/useMaster";
import { flattenObject, humanizeText, showToast } from "utils/utility";
import { useAutoTicketingData } from "libs/data-access";

interface IAutoTicketingDetails {
    supplierIds: number[];
    airlineIds: number[];
    startDate: string | null;
    endDate: string | null;
    flightType: "DOMESTIC" | "INTERNATIONAL";
}

interface IAutoTicketingRules {
    supplierIds: "YES" | "NO";
    airlineIds: "YES" | "NO";
    startDate: "YES" | "NO";
    endDate: "YES" | "NO";
    flightType: "YES" | "NO";
}

interface IAutoTicketingErrors {
    supplierIds: string | undefined;
    airlineIds: string | undefined;
    startDate: string | undefined;
    endDate: string | undefined;
    flightType: string | undefined;
}

interface IAutoTicketingForm {
    isLoading: boolean;
    details: IAutoTicketingDetails;
    rules: IAutoTicketingRules;
    errors: IAutoTicketingErrors;
}

export default function useAutoTicketing() {

    const {
        getAutoTicketingData,
        postAutoTicketingData
    } = useAutoTicketingData();
    const {
        supplierList,
        getSupplierList,
        airlinesList,
        getAirlinesBySuppliers,
    } = useMaster();

    const [activeTab, setActiveTab] = useState<"DOMESTIC" | "INTERNATIONAL">("DOMESTIC");

    const [domesticRule, setDomesticRule] = useState<IAutoTicketingForm>({
        isLoading: false,
        details: {
            supplierIds: [],
            airlineIds: [],
            startDate: null,
            endDate: null,
            flightType: "DOMESTIC"
        },
        rules: {
            supplierIds: "YES",
            airlineIds: "YES",
            startDate: "YES",
            endDate: "YES",
            flightType: "YES"
        },
        errors: {
            supplierIds: undefined,
            airlineIds: undefined,
            startDate: undefined,
            endDate: undefined,
            flightType: undefined
        }
    });

    const [internationalRule, setInternationalRule] = useState<IAutoTicketingForm>({
        isLoading: false,
        details: {
            supplierIds: [],
            airlineIds: [],
            startDate: null,
            endDate: null,
            flightType: "INTERNATIONAL"
        },
        rules: {
            supplierIds: "YES",
            airlineIds: "YES",
            startDate: "YES",
            endDate: "YES",
            flightType: "YES"
        },
        errors: {
            supplierIds: undefined,
            airlineIds: undefined,
            startDate: undefined,
            endDate: undefined,
            flightType: undefined
        }
    });


    useEffect(() => {
        getSupplierList("FLIGHT");
        getAutoTicketingRule("DOMESTIC");
        getAutoTicketingRule("INTERNATIONAL");
    }, []);


    // HANDLER FUNCTIONS
    const getCurrentRule = () => activeTab === "DOMESTIC" ? domesticRule : internationalRule;

    const setCurrentRule = (updater: (prev: IAutoTicketingForm) => IAutoTicketingForm) => {
        if (activeTab === "DOMESTIC") {
            setDomesticRule(updater);
        } else {
            setInternationalRule(updater);
        };
    };

    const handleUpdateRule = (
        key: keyof IAutoTicketingDetails,
        value: number[] | string | null
    ) => {
        setCurrentRule((prev) => ({
            ...prev,
            details: {
                ...prev.details,
                [key]: value
            },
            errors: {
                ...prev.errors,
                [key]: undefined
            }
        }));
    };

    const validateAutoTicketing = (flightType: "DOMESTIC" | "INTERNATIONAL") => {
        const currentRule = flightType === "DOMESTIC" ? domesticRule : internationalRule;

        const errors: IAutoTicketingErrors = {
            supplierIds: (
                currentRule.rules.supplierIds === "YES"
                &&
                !currentRule.details.supplierIds.length
            ) ? "Supplier is mandatory" : undefined,
            airlineIds: (
                currentRule.rules.airlineIds === "YES"
                &&
                !currentRule.details.airlineIds.length
            ) ? "Airline is mandatory" : undefined,
            startDate: (
                currentRule.rules.startDate === "YES"
                &&
                !currentRule.details.startDate
            ) ? "Start date is mandatory" : undefined,
            endDate: (
                currentRule.rules.endDate === "YES"
                &&
                !currentRule.details.endDate
            ) ? "End date is mandatory" : undefined,
            flightType: (
                currentRule.rules.flightType === "YES"
                &&
                !currentRule.details.flightType
            ) ? "Flight type is mandatory" : undefined
        };

        if (flightType === "DOMESTIC") {
            setDomesticRule(prev => ({
                ...prev,
                errors: errors
            }));
        } else {
            setInternationalRule(prev => ({
                ...prev,
                errors: errors
            }));
        }

        const isValid = Object.values(flattenObject(errors)).every(val => !val);
        return isValid;
    };


    // API CALLS
    const getAutoTicketingRule = async (flightType: "DOMESTIC" | "INTERNATIONAL") => {
        const setRule = flightType === "DOMESTIC" ? setDomesticRule : setInternationalRule;

        setRule(prev => ({ ...prev, isLoading: true }));

        const response = await getAutoTicketingData(`?flightType=${flightType}`);

        if (response.isSuccess) {
            const data = response.data?.data as IAutoTicketingDetails;

            setRule(prev => ({
                ...prev,
                details: data
            }));

            if (data.supplierIds?.length) {
                getAirlinesBySuppliers(data.supplierIds.map(each => String(each)));
            };
        } else {
            console.log(`Error while fetching ${flightType.toLowerCase()} auto ticketing rule: `, response);
            if (response.status === 404) {
                showToast({
                    type: "warning",
                    title: "Rule not found!",
                    message: `Please set a ${flightType.toLowerCase()} auto ticketing rule`
                });
            } else {
                showToast({
                    type: "error",
                    message: response.message || `Could not fetch ${flightType.toLowerCase()} auto ticketing rule!`
                });
            };
        };

        setRule(prev => ({ ...prev, isLoading: false }));
    };

    const saveAutoTicketingRule = async (flightType: "DOMESTIC" | "INTERNATIONAL") => {
        if (!validateAutoTicketing(flightType)) return;

        const currentRule = flightType === "DOMESTIC" ? domesticRule : internationalRule;
        const setRule = flightType === "DOMESTIC" ? setDomesticRule : setInternationalRule;

        setRule(prev => ({ ...prev, isLoading: true }));

        const payload: IAutoTicketingDetails = {
            supplierIds: currentRule.details.supplierIds,
            airlineIds: currentRule.details.airlineIds,
            startDate: currentRule.details.startDate,
            endDate: currentRule.details.endDate,
            flightType: currentRule.details.flightType
        };

        const response = await postAutoTicketingData(`?flightType=${flightType}`, payload);

        if (response.isSuccess) {
            showToast({
                type: "success",
                message: `${humanizeText(flightType)} auto ticketing rule saved successfully!`
            });
            getAutoTicketingRule(flightType);
        } else {
            console.log(`Error while saving ${flightType.toLowerCase()} auto ticketing rule: `, response);
            showToast({
                type: "error",
                message: response.message || `Could not save ${flightType.toLowerCase()} auto ticketing rule!`
            });
        };

        setRule(prev => ({ ...prev, isLoading: false }));
    };


    return {
        supplierList,
        airlinesList,
        getAirlinesBySuppliers,

        activeTab,
        setActiveTab,
        domesticRule,
        internationalRule,
        getCurrentRule,
        handleUpdateRule,
        saveAutoTicketingRule
    };
}